import { useState } from 'react';
import { ArrowDownUp, ChevronDown, Loader2, Package, PackagePlus } from 'lucide-react';
import type {
  InventoryCategoryGroup,
  InventoryCategoryReportVariant,
} from '../../lib/inventoryCategoryReport';
import { formatCurrency } from '../../lib/utils';

interface InventoryMovementByCategoryPanelProps {
  variant: InventoryCategoryReportVariant;
  categories: InventoryCategoryGroup[];
  totalAmount: number;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

const PANEL_COPY: Record<
  InventoryCategoryReportVariant,
  {
    title: string;
    subtitle: string;
    ariaLabel: string;
    emptyTitle: string;
    emptyHint: string;
    icon: typeof ArrowDownUp;
  }
> = {
  sales: {
    title: 'Sales',
    subtitle: 'Product sales · This month',
    ariaLabel: 'Product sales by category',
    emptyTitle: 'No product sales',
    emptyHint: 'Product sales this month will appear here by category.',
    icon: ArrowDownUp,
  },
  consumed: {
    title: 'Consumed',
    subtitle: 'Internal withdrawals · This month',
    ariaLabel: 'Consumed stock by category',
    emptyTitle: 'No consumed stock',
    emptyHint: 'Internal withdrawals this month will appear here by category.',
    icon: ArrowDownUp,
  },
  purchased: {
    title: 'Purchased',
    subtitle: 'Stock received · This month',
    ariaLabel: 'Purchased stock by category',
    emptyTitle: 'No purchases',
    emptyHint: 'Stock received this month will appear here by category.',
    icon: PackagePlus,
  },
};

function categoryKey(category: InventoryCategoryGroup): string {
  return category.categoryId ?? '__uncategorized__';
}

/** Expandable sales / consumed report grouped by category. */
export default function InventoryMovementByCategoryPanel({
  variant,
  categories,
  totalAmount,
  loading = false,
  error = null,
  onRetry,
}: InventoryMovementByCategoryPanelProps) {
  const copy = PANEL_COPY[variant];
  const HeaderIcon = copy.icon;
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());

  const toggleCategory = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div
      className="relative z-0 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-cc-inset
      aria-label={copy.ariaLabel}
      style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', system-ui, sans-serif" }}
    >
      <header
        className="flex shrink-0 items-center gap-2.5 px-3 py-2.5 sm:px-3.5 sm:py-3"
        data-cc-inset-header
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-gray-50 text-gray-600 sm:h-9 sm:w-9">
          <HeaderIcon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className="truncate text-[15px] font-semibold text-gray-900 sm:text-[16px] leading-tight"
            style={{ letterSpacing: '-0.01em' }}
          >
            {copy.title}
          </h3>
          <p className="mt-0.5 truncate text-[12px] text-gray-500 sm:text-[12px]">{copy.subtitle}</p>
        </div>
        {!loading && categories.length > 0 ? (
          <p className="shrink-0 text-right">
            <span className="block text-[11px] font-medium text-gray-500">
              Total
            </span>
            <span
              className="block text-[15px] font-bold tabular-nums text-gray-900 sm:text-[16px]"
              style={{ letterSpacing: '-0.02em' }}
            >
              {formatCurrency(totalAmount)}
            </span>
          </p>
        ) : null}
      </header>

      {loading ? (
        <div className="flex flex-1 items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex flex-1 flex-col items-center justify-center px-3 py-4 text-center">
          <p className="text-xs text-red-600 sm:text-sm">{error}</p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 text-[11px] font-semibold text-blue-600 sm:text-xs"
            >
              Retry
            </button>
          ) : null}
        </div>
      ) : categories.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-5 text-center sm:px-4 sm:py-6">
          <Package className="mb-2 h-9 w-9 text-gray-200 sm:h-10 sm:w-10" strokeWidth={1.25} aria-hidden />
          <p className="text-[14px] font-medium text-gray-700 sm:text-[15px]">{copy.emptyTitle}</p>
          <p className="mt-1 max-w-[240px] text-[12px] text-gray-500 sm:mt-1 sm:text-[13px]">{copy.emptyHint}</p>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <ul className="flex-1 min-h-0 overflow-y-auto ios-momentum-scroll px-2.5 pb-2 pt-1 sm:px-3 sm:pb-3">
            {categories.map((category) => {
              const key = categoryKey(category);
              const expanded = expandedKeys.has(key);
              const itemCount = category.items.length;

              return (
                <li
                  key={key}
                  className="border-b border-gray-100/90 last:border-b-0 py-2 first:pt-0 sm:py-2.5"
                >
                  <div className="flex items-start gap-1.5 sm:gap-2">
                    <button
                      type="button"
                      onClick={() => toggleCategory(key)}
                      className="min-w-0 flex-1 touch-manipulation text-left"
                      aria-expanded={expanded}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <p
                        className="truncate text-[13px] font-semibold text-gray-900 sm:text-[14px]"
                        style={{ letterSpacing: '-0.005em' }}
                      >
                        {category.categoryName}
                      </p>
                      <p
                        className="mt-0.5 text-[15px] font-bold tabular-nums text-gray-900 sm:text-[16px]"
                        style={{ letterSpacing: '-0.02em' }}
                      >
                        {formatCurrency(category.totalAmount)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-gray-500 sm:text-[12px] tabular-nums">
                        {itemCount} item{itemCount === 1 ? '' : 's'} · {category.totalUnits} unit
                        {category.totalUnits === 1 ? '' : 's'}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCategory(key)}
                      className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 active:scale-95 touch-manipulation"
                      aria-label={expanded ? `Collapse ${category.categoryName}` : `Expand ${category.categoryName}`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${expanded ? '' : '-rotate-90'}`}
                        aria-hidden
                      />
                    </button>
                  </div>

                  {expanded ? (
                    <ul className="mt-2 space-y-1.5 pl-1 sm:mt-2 sm:space-y-1.5 sm:pl-1.5">
                      {category.items.map((item) => (
                        <li key={item.itemId}>
                          <div className="flex items-start justify-between gap-2 rounded-xl border border-gray-200/80 bg-white/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                            <div className="min-w-0">
                              <p className="truncate text-[12px] font-semibold text-gray-900 sm:text-[13px]">
                                {item.name}
                              </p>
                              <p className="mt-0.5 text-[11px] text-gray-500 sm:text-[12px] tabular-nums">
                                {item.quantity} {item.unit}
                              </p>
                            </div>
                            <p
                              className="shrink-0 text-[13px] font-bold tabular-nums text-gray-800 sm:text-[13px]"
                              style={{ letterSpacing: '-0.01em' }}
                            >
                              {formatCurrency(item.amount)}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
